import { Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { ErrorMessage, LoginTypes } from 'shared-library';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { IAppConfig } from '@app/store/models/config.interface';
import { APP_CONFIG } from 'src/app/modules/core/config/config';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AuthDataForm } from '@app/store/models/auth-data.interface';
import { selectAuthState } from '../../store/selectors/auth.selectors';
import { SessionInfo } from '@app/store/models/session-info.interface';
import { CoreService } from 'src/app/modules/core/services/core.service';
import { FunctionsService } from 'src/app/modules/core/services/functions.service';
import { FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

@Component({
  selector: 'app-auth-resetpass',
  templateUrl: './auth-resetpass.component.html',
  styleUrls: ['./../auth.global.scss', './auth-resetpass.component.scss']
})
export class AuthResetpassComponent implements OnInit, OnDestroy {

  error = ""
  success = ""
  token_id = ""
  processing = false
  mode: "start" | "end" = "start"
  resetpass_form: FormGroup<AuthDataForm>
  private subscriptions: Subscription[] = []
  authState$ = this.store.select(selectAuthState);

  session_info: SessionInfo
  activating_ots_qr: string
  activation_code_ots_qr: string

  constructor(
    private store: Store,
    private router: Router,
    private httpClient: HttpClient,
    private coreService: CoreService,
    private formBuilder: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private functionsService: FunctionsService,
    @Inject(APP_CONFIG) private readonly config: IAppConfig
  ) {
    this.token_id = this.activatedRoute.snapshot.queryParams.code
    if (this.token_id) {
      this.checkToken()
    }
    this.subscriptions.push(this.coreService.session_dataObservable.subscribe(si => this.session_info = si))
    this.subscriptions.push(this.authState$.subscribe(ls => {
      this.activating_ots_qr = ls.data.activating_ots_qr
      this.activation_code_ots_qr = ls.data.activation_code_ots_qr
    }))
  }

  ngOnInit() {
    this.fillFormData()
  }

  ngOnDestroy(): void {
    this.subscriptions.map(sub => sub.unsubscribe())
  }

  private fillFormData() {
    if (this.mode === 'start') {
      this.resetpass_form = this.formBuilder.group({
        id_token: [""],
        cpassword: [""],
        recaptcha_token: [""],
        code: [this.token_id],
        password_strength: [0],
        referral_id: [""],
        activating_ots_qr: [""],
        activation_code_ots_qr: [""],
        auth_type: [LoginTypes.EMAIL],
        email: ["", [Validators.email, Validators.required]],
        name: [""],
        mobile: [""],
        company: [""],
        password: [""],
        timezone: [Intl.DateTimeFormat().resolvedOptions().timeZone],
        otp: this.formBuilder.array(["", "", "", "", "", ""].map(otp => this.formBuilder.control(otp))),

      })
      return
    }

    const passwordErrorValidator: ValidatorFn = (control: FormGroup<AuthDataForm>): ValidationErrors | null => {
      const password = control.controls.password
      const repeatPassword = control.controls.cpassword
      return password.value != repeatPassword.value ? { 'passwordError': true } : null;
    };

    this.resetpass_form = this.formBuilder.group({
      email: [""],
      id_token: [""],
      cpassword: [""],
      recaptcha_token: [""],
      code: [this.token_id],
      password_strength: [0],
      activating_ots_qr: [""],
      activation_code_ots_qr: [""],
      auth_type: [LoginTypes.EMAIL],
      password: ["", [Validators.required]],

      name: [""],
      mobile: [""],
      company: [""],
      referral_id: [""],
      timezone: [Intl.DateTimeFormat().resolvedOptions().timeZone],
      otp: this.formBuilder.array(["", "", "", "", "", ""].map(otp => this.formBuilder.control(otp))),

    }, { validators: passwordErrorValidator })

    this.subscriptions.push(this.resetpass_form.controls.password.valueChanges.subscribe(vv => this.error = this.success = ""))
    this.subscriptions.push(this.resetpass_form.controls.cpassword.valueChanges.subscribe(vv => this.error = this.success = ""))
    this.token_id = ""

  }

  private async checkToken() {
    this.processing = true
    try {
      const recaptcha_token = await this.coreService.getRecaptchaToken()
      await this.httpClient.get(this.config.apiEndpoints.auth.resetpassword, { params: { code: this.token_id, recaptcha_token }, headers: { "skip-message": "true" } }).toPromise()
      this.mode = 'end'
      this.fillFormData()
    } catch (error) {
      this.error = error?.error?.error ? (ErrorMessage[error?.error?.error] || error.error.error) : "Expired password reset link"
      this.token_id = ""
    }
    this.processing = false
  }

  async resetPass() {
    this.resetpass_form.markAllAsTouched()
    if (this.processing || !this.resetpass_form.valid) return
    const reset_data = this.resetpass_form.value
    this.processing = true
    try {
      reset_data.recaptcha_token = await this.coreService.getRecaptchaToken()
      this.functionsService.logToConsole(reset_data)
      if (this.mode === 'start') {
        await this.httpClient.post(this.config.apiEndpoints.auth.resetpassword, reset_data, { headers: { "skip-message": "true" } }).toPromise()
        this.success = `A password reset email has been sent to <b>${reset_data.email}</b>`
      } else {
        await this.httpClient.patch(this.config.apiEndpoints.auth.resetpassword, reset_data, { headers: { "skip-message": "true" } }).toPromise()
        this.success = `Password reset successful, you'll be automatically redirected shortly.`
      }
      this.fillFormData()
      setTimeout(() => {
        this.router.navigateByUrl(`/auth/signin`)
      }, 3000)
    } catch (error) {
      if (typeof error?.error === typeof this.error) this.error = ErrorMessage[error.error] || error.error
      else if (typeof error?.error?.error === typeof this.error) this.error = ErrorMessage[error.error.error] || error.error.error
    }
    this.processing = false
  }

}
